import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import { FileUploader } from "react-drag-drop-files";
import { useAuth } from "../contexts/AuthContext";
import { useSnackbar } from "../contexts/SnackbarProvider";
import { viewableUrlFile } from "../utils/utilityFunction";
import ImageMapper from "react-img-mapper";

const AddButtonToImage = ({ image, setNeedRefresh, unitId }) => {
  const Auth = useAuth();
  const { setSnackbarDetail } = useSnackbar();
  const [radius, setRadius] = useState(12);
  const [coord, setCoord] = useState({});
  const [audio, setAudio] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [audioAnchorEl, setAudioAnchorEl] = useState(null);
  const [audioFile, setAudioFile] = useState();

  const [mapAreas, setMapAreas] = useState(() => {
    let lastButtons = image?.buttons?.map((btn) => ({
      id: btn.id,
      shape: "circle",
      coords: [btn.x, btn.y, btn.r],
      preFillColor: "fff9",
    }));
    return {
      name: `btns-${image.id}`,
      areas: [
        {
          id: 1,
          shape: "circle",
          coords: [-1, -1, radius],
          preFillColor: "fff9",
        },
        ...(lastButtons ? lastButtons : ""),
      ],
    };
  });

  const updateMapArea = useCallback(
    (id, coords) => {
      setCoord({ x: coords[0], y: coords[1] });
      const areas = mapAreas.areas.map((item) =>
        item.id === id ? { ...item, coords } : item
      );
      setMapAreas({
        name: mapAreas.name,
        areas,
      });
    },
    [mapAreas.areas, mapAreas.name]
  );

  const showAudioFile = useCallback(
    (area, index, evt) => {
      let tempBtn = image?.buttons.find((btn) => btn.id === area.id);
      let tempAudio = viewableUrlFile(tempBtn?.audio_url);
      setAudioFile({ id: area.id, name: tempAudio });
      setAudioAnchorEl({
        x:
          evt.currentTarget.getBoundingClientRect().left +
          evt.nativeEvent.layerX -
          150,
        y:
          evt.currentTarget.getBoundingClientRect().top +
          evt.nativeEvent.layerY -
          250,
      });
    },

    [image?.buttons]
  );
  const handleUpdateMapArea = useCallback(
    (evt) => {
      setAudioAnchorEl({
        x:
          evt.currentTarget.getBoundingClientRect().left +
          evt.nativeEvent.layerX,
        y:
          evt.currentTarget.getBoundingClientRect().top +
          evt.nativeEvent.layerY,
      });
      updateMapArea(1, [
        evt.nativeEvent.layerX,
        evt.nativeEvent.layerY,
        radius,
      ]);
    },
    [radius, updateMapArea]
  );

  function addButton(e) {
    e.preventDefault();
    setIsUploading(true);
    let formData = new FormData();
    formData.append("x", mapAreas?.areas?.[0].coords?.[0]);
    formData.append("y", mapAreas?.areas?.[0].coords?.[1]);
    formData.append("r", radius);
    formData.append("audio", audio);
    formData.append("imageId", image.id);
    formData.append("unitId", unitId);
    Auth.AuthenticatedServerCall({
      method: "post",
      isFormData: true,
      body: formData,
      entity: "api/buttons",
    })
      .then((res) => {
        setSnackbarDetail({
          open: true,
          message: "button add successfully.",
          severity: "success",
        });
        e.target.reset();
        setAudio(null);
        setIsUploading(false);
        setNeedRefresh(true);
        setAudioAnchorEl(null);
      })
      .catch((e) => {
        setSnackbarDetail({
          open: true,
          message: "error on adding button",
          severity: "error",
        });
        setIsUploading(false);
      });
  }

  function deleteButton(audioId) {
    setIsUploading(true);

    Auth.AuthenticatedServerCall({
      method: "delete",
      entity: `api/buttons/${audioId}`,
    })
      .then((res) => {
        setSnackbarDetail({
          open: true,
          message: "button deleted successfully.",
          severity: "success",
        });
        setAudioFile(null);
        setAudio(null);
        setIsUploading(false);
        setNeedRefresh(true);
        setAudioAnchorEl(null);
      })
      .catch((e) => {
        setSnackbarDetail({
          open: true,
          message: "error on deleting button",
          severity: "error",
        });
        setIsUploading(false);
      });
  }

  React.useEffect(() => {
    setMapAreas((prev) => {
      const areas = prev.areas.map((item) => ({
        ...item,
        coords: [item.coords[0], item.coords[1], radius],
      }));
      return {
        name: prev.name,
        areas,
      };
    });
  }, [radius]);

  return (
    <>
      <Box
        sx={{
          mt: 3,
          maxWidth: "100%",
          overflow: "auto",
          position: "relative",
        }}
      >
        <ImageMapper
          src={`${process.env.REACT_APP_FILE_LOCATION}${viewableUrlFile(
            image?.image_url
          )}`}
          onImageClick={handleUpdateMapArea}
          map={mapAreas}
          onClick={showAudioFile}
          active
        />
      </Box>
      {audioAnchorEl?.x && (
        <Box
          component="form"
          onSubmit={addButton}
          sx={{
            backgroundColor: "white",
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            position: "absolute",
            left: audioAnchorEl.x,
            top: audioAnchorEl.y + 60,
            zIndex: 10,
            borderRadius: 2,
            p: 2,
          }}
        >
          <IconButton
            onClick={() => {
              setAudioAnchorEl(null);
              setAudioFile();
            }}
            sx={{ position: "absolute", left: "0px", top: "0px" }}
          >
            X
          </IconButton>
          {audioFile?.id ? (
            <Box>
              <h3>{audioFile?.name}</h3>
              <Button onClick={() => deleteButton(audioFile.id)}>delete</Button>
            </Box>
          ) : (
            <>
              <TextField
                type="number"
                value={radius}
                label="radius"
                onChange={(e) => {
                  let number = parseInt(e.target.value);
                  if (number < 3) {
                    setRadius(3);
                    return;
                  }
                  setRadius(number);
                }}
                sx={{ mb: 2 }}
              />

              <FileUploader
                handleChange={(img) => setAudio(img)}
                name="file"
                types={["mp3", "wav", "m4a", "wma", "aac", "mpeg", "mp4"]}
              />
              {audio && (
                <Typography gutterBottom variant="caption">
                  {audio?.name}
                </Typography>
              )}

              <Button
                variant="contained"
                type="submit"
                disabled={!coord.x || !coord.y || !audio?.name || isUploading}
              >
                {isUploading ? <CircularProgress /> : "save"}
              </Button>
            </>
          )}
        </Box>
      )}
    </>
  );
};

export default AddButtonToImage;
